export interface BlotterLoadStateInput {
  /** react-query isLoading for the windowed list — false while the query is DISABLED. */
  tradesLoading: boolean;
  /** The list query's error object (null when OK). TanStack v5 sets this on a failed
   *  refetch even while cached data persists — hence hasTradesData below. */
  tradesError: Error | null;
  /** The list has at least one successful page (totalCount !== undefined). */
  hasTradesData: boolean;
  /** The active scope needs the CP registry resolved (a per-CP view or a sidebar CP filter). */
  needsCpRegistry: boolean;
  /** The registry has resolved — counterparties !== undefined (cached data counts). */
  cpRegistryReady: boolean;
  /** The registry query's error object (null when OK). */
  cpRegistryError: Error | null;
}

export interface BlotterLoadState {
  /** Show the skeleton. */
  isLoading: boolean;
  /** Show the error state. */
  isError: boolean;
  /** The error object to hand TradesTable — null unless the blotter genuinely can't show
   *  content (kept as the single source of truth so callers don't re-derive the predicate). */
  error: Error | null;
}

/**
 * Loading / error for the windowed blotter, accounting for the CP registry the list query
 * is gated on (useTradesInfinite is disabled until the registry resolves for a CP-scoped
 * tab, so it doesn't fire a known-empty counterparty_ids=-1 request).
 *
 * A DISABLED TanStack v5 query reports isLoading=false, and — critically — v5's reducer
 * flips a query to `error` on a failed fetch even when it still holds cached `data`
 * (`status:"error"` is set unconditionally). So the rule for the whole class is: show the
 * error state ONLY when the blotter genuinely has nothing to show, never over a working
 * cached book (kvi-r3 #1, kvi-r4 #1):
 *  - registry PENDING → loading (skeleton, not the empty-state);
 *  - registry DOWN (failed AND no cached registry) → error (the list is gated off, so
 *    there's no book to show) — not an eternal skeleton;
 *  - registry STALE (cached data + failed refetch) → serve the book, no error;
 *  - list errored → error ONLY before its first successful page; a background-refetch
 *    failure over cached pages keeps serving (same trigger as the registry: the list
 *    polls on a 5-min interval + window focus).
 */
export const resolveBlotterLoadState = (i: BlotterLoadStateInput): BlotterLoadState => {
  const registryPending = i.needsCpRegistry && !i.cpRegistryReady && i.cpRegistryError === null;
  const registryDown = i.needsCpRegistry && !i.cpRegistryReady && i.cpRegistryError !== null;
  const isLoading = i.tradesLoading || registryPending;

  let error: Error | null = null;
  if (!isLoading) {
    if (registryDown) {
      error = i.cpRegistryError;
    } else if (!i.hasTradesData) {
      // No successful page yet → a genuine load failure, not a refetch over cached data.
      error = i.tradesError;
    }
  }
  return { isLoading, isError: error !== null, error };
};
